<template>
  <div id="dkt-mixin"></div>
</template>

<script>
import idpAuth from '@/components/shared/idpAuth';
import cookieHandler from '@/cookie_handler';
// import LessonStarterMixins from '@/components/shared/lessons/mixins/LessonStarterMixins';
import GenericModal from '@/components/shared/elements/modal/GenericModal';
import KretaAccountTransferModal from '@/components/shared/elements/modal/KretaAccountTransferModal';
import { fromLongToShort } from '@/components/shared/languageCodes';
import { mapActions, mapMutations, mapGetters } from 'vuex';

export default {
  name: 'DKTMixin',
  mixins: [idpAuth],
  data() {
    return {
      baseLearnedLanguage: undefined,
      cancelMemberShipErrorObject: undefined,
    };
  },
  computed: {
    ...mapGetters({
      deviceName: 'device/deviceName',
      deviceToken: 'device/deviceToken',
      getLang: 'settings/getSiteLanguage',
      isKretaUser: 'users/isKretaUser',
      loggedInUserProfile: 'users/loggedInUserProfile',
      getAuthToken: 'getAuthToken',
      getDktOptions: 'settings/getDktOptions',
      getSubDomain: 'settings/getSubDomain',
      getSelectedLearnedLanguage: 'settings/getSelectedLearnedLanguage',
    }),
    user() {
      return this.loggedInUserProfile.user;
    },
    userProfileScopes() {
      if (!this.hasToken) {
        return [];
      }
      const scopes = [
        this.$constants.USER_SCOPES.USER,
        this.$constants.USER_SCOPES.RESULTS,
        this.$constants.USER_SCOPES.STATISTICS,
        this.$constants.USER_SCOPES.LANGUAGE_PROGRESS,
      ];
      if (!this.isKretaUser) {
        scopes.push(this.$constants.USER_SCOPES.LEAGUE);
        scopes.push(this.$constants.USER_SCOPES.FRIENDS);
      }
      return scopes;
    },
    userLanguage() {
      const lang = fromLongToShort[this.getLang];
      if (!lang) {
        return 'en';
      }
      return lang;
    },
  },
  methods: {
    ...mapActions({
      getAssignmentByDktId: 'classroom/getAssignmentByDktId',
      authWithDKTSSO: 'users/authWithDKTSSO',
      setDeviceName: 'device/setDeviceName',
      setDeviceToken: 'device/setDeviceToken',
      setAdvertisingToken: 'device/setAdvertisingToken',
      getClassroomsByStudent: 'classroom/getClassroomsByStudent',
      userLogout: 'users/logout',
      logoutBackend: 'users/logoutBackend',
      setDktOptions: 'settings/setDktOptions',
      getLesson: 'contents/getLesson',
      setStartFromClass: 'settings/setStartFromClass',
      profileUpdate: 'users/profileUpdate',
      setMobileSupportModalStatus: 'settings/setMobileSupportModalStatus',
      getUserProfile: 'users/getUserProfile',
    }),
    ...mapMutations({
      setApiToken: 'users/SET_SESSION_TOKEN',
      setProfileUser: 'users/SET_PROFILE_USER',
      storeToken: 'storeToken',
    }),
    updateUserLanguage(dktOptions) {
      return new Promise((resolve) => {
        if (dktOptions.fromDkt) {
          resolve();
        } else if (this.baseLearnedLanguage) {
          this.profileUpdate({ learned_language_id: this.baseLearnedLanguage })
            .then(() => {
              resolve();
            })
            .catch(() => resolve());
        }
      });
    },
    logoutUserMethod() {
      return new Promise((resolve) => {
        Promise.all([this.logoutBackend(), this.userLogout(true)])
          .then(() => {
            resolve();
          })
          .catch(() => {
            resolve();
          });
      });
    },
    disableAllCookieIfNeeded() {
      if (window.CookieFirst) {
        window.CookieFirst.declineAllCategories();
      }
    },
    startLesson(lesson, additionalInfo) {
      if (lesson.id !== undefined) {
        let teachbotRoute = `/conversations/${lesson.id}?${
          additionalInfo != null && additionalInfo.thematicGroupId != null
            ? `tgi=${additionalInfo.thematicGroupId}&ti=${additionalInfo.thematicId}`
            : ''
        }`;
        let route = `/lessons/${lesson.id}?${
          additionalInfo != null && additionalInfo.thematicGroupId != null
            ? `tgi=${additionalInfo.thematicGroupId}&ti=${additionalInfo.thematicId}`
            : ''
        }`;
        if (additionalInfo && additionalInfo.assignmentId) {
          if (route.slice(-1) === '?') {
            route += `assignmentId=${additionalInfo.assignmentId}`;
          } else {
            route += `&assignmentId=${additionalInfo.assignmentId}`;
          }
          if (teachbotRoute.slice(-1) === '?') {
            teachbotRoute += `?assignmentId=${additionalInfo.assignmentId}`;
          } else {
            teachbotRoute += `?assignmentId=${additionalInfo.assignmentId}`;
          }

          if (additionalInfo && additionalInfo.classId) {
            route += `&classId=${additionalInfo.classId}`;
            teachbotRoute += `&classId=${additionalInfo.classId}`;
          }
        }
        if (location.hash) {
          teachbotRoute += location.hash;
        }
        this.setStartFromClass(true);
        const endRoute =
          lesson.lessonType == 'chatbot_goal_group' ? teachbotRoute : route;
        location.replace(process.env.VUE_APP_KRETA_TIMEOUT_REDIRECT_LANDING.concat(endRoute));
        this.disableAllCookieIfNeeded();
        // this.$router.push(
        // );
      }
    },
    kretaToKretaUserAccountChange(payload) {
      this.$eventBus.showModal({
        bind: {
          is: GenericModal,
          hasExit: false,
          lock: true,
          titleText: this.$t('app.dkt.kretaToKreta.modal.title'),
          description: this.$t('app.dkt.kretaToKreta.modal.description'),
          okText: this.$t('app.dkt.kretaToKreta.modal.okText'),
          okAction: async () => {
            await this.setDktUserFromResponse(payload);
            this.$eventBus.closeModal();
          },
          cancelText: this.$t('app.dkt.kretaToKreta.modal.cancelText'),
          cancelAction: () => {
            this.$router.push('/');
            this.$eventBus.closeModal();
          },
          escAction: () => {
            this.$router.push('/');
            this.$eventBus.closeModal();
          },
        },
      });
    },
    requestData(token) {
      const loginDetails = {
        access_token: token,
        league_code: null,
        country_code: this.userLanguage,
        learn_to_language_code: this.userLanguage,
        platform: 'web',
        device_name: this.deviceName,
        device_token: this.deviceToken,
      };
      const inviterCode = localStorage.getItem('invitationCode');
      if (inviterCode) loginDetails.inviter_code = inviterCode;

      return loginDetails;
    },
    dktRedirect(
      needModal = true,
      accessToken = undefined,
      needNavigation = true,
      customLocation = undefined,
    ) {
      let dktRedirect = `${location.origin}${location.pathname}`;
      if (customLocation) {
        dktRedirect = `${location.origin}${customLocation}`;
      }
      const currentLocation = location.pathname.split('/');
      if (
        currentLocation &&
        currentLocation[1] &&
        ['lessons', 'conversations', 'topics', 'classes', 'skills'].includes(
          currentLocation[1],
        )
      ) {
        dktRedirect = `${location.origin}`;
      }
      const paramsObject = {
        needModal,
        accessToken,
        redirect: dktRedirect,
      };
      const queryParams = btoa(JSON.stringify(paramsObject));
      if (needNavigation) {
        this.$eventBus.closeModal();
        // if (!this.getSubDomain && location.hostname != 'localhost') {
        //   this.$router.replace(`/dkt/redirect?query_params=${queryParams}`);
        // } else {
          location.href = `${process.env.VUE_APP_IDP_REDIRECT_URL}?query_params=${queryParams}`;
        // }
      }
      return queryParams;
    },
    getAuthCode(
      needModal = true,
      accessToken = undefined,
      redirect = undefined,
    ) {
      return new Promise((resolve, reject) => {
        this.getIdpAuthorizationCode(accessToken, redirect)
          .then(() => {
            resolve();
          })
          .catch((err) => {
            if (needModal) {
              this.showGeneralErrorModal(redirect);
            }
            reject(err);
            this.setMobileSupportModalStatus(false);
            // console.error('Error during requiring code:', err);
          });
      });
    },
    getOAuth2Token(needModal = true) {
      return new Promise((resolve, reject) => {
        this.getIdpToken()
          .then((token) => {
            resolve(token);
          })
          .catch((err) => {
            if (needModal) {
              this.showGeneralErrorModal();
            }
            this.setMobileSupportModalStatus(false);
            console.error('Error during requiring access token:', err);
            reject(err);
          });
      });
    },
    deleteAllCookies() {
      const cookies = document.cookie.split(';');
      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });
    },
    loginWithIDP(
      token = undefined,
      navigateTo = '/',
      overwriteUser = true,
      navigateRegisteredUserTo,
      afterGetProfileCallback = null,
    ) {
      return new Promise(async (resolve, reject) => {
        if (token) {
          if (!this.devName && !this.devToken && !this.advToken) {
            const userAgent = navigator.userAgent.substring(0, 230);
            const date = this.$moment().format('YYYY-MM-DD HH:mm');
            this.setDeviceName(`${userAgent} ${date}`);
            this.setDeviceToken(this.$uuid());
            this.setAdvertisingToken(this.$uuid());
          }
          if (['en', 'de'].includes(this.getSelectedLearnedLanguage)) {
            this.baseLearnedLanguage = this.getSelectedLearnedLanguage;
          }
          const isKozigIn = ((window.$cookies.get('logged-in') == true || window.$cookies.get('logged-in') == 'true'));
          const isKozigOut = ((window.$cookies.get('logged-out') == true || window.$cookies.get('logged-out') == 'true'));
          this.authWithDKTSSO({ ...this.requestData(token), overwriteUser })
            .then(async (payload) => {
              if (payload.token) {
                if (typeof afterGetProfileCallback === 'function') {
                  afterGetProfileCallback(payload);
                }
                if (
                  ((this.getAuthToken != null &&
                  this.loggedInUserProfile.user.id &&
                  (payload.id !== this.loggedInUserProfile.user.id)) || isKozigIn) && !isKozigOut
                ) {
                  if (this.loggedInUserProfile.user.isDktMember && !isKozigIn) {
                    this.kretaToKretaUserAccountChange(payload);
                  } else {
                    this.transferAccounts(payload);
                  }
                } else {
                  await this.setDktUserFromResponse(payload, false, false);
                  if (
                    this.getDktOptions.lessonId &&
                    this.getDktOptions.dktAssignmentId
                  ) {
                    this.handle2AFlowMethod();
                  } else if (payload.registrationStatus === 'registered') {
                    if (navigateRegisteredUserTo) {
                      // location.replace(document.referrer);
                      location.replace(process.env.VUE_APP_KRETA_TIMEOUT_REDIRECT_LANDING);
                      this.disableAllCookieIfNeeded();
                      // this.$router.replace(navigateRegisteredUserTo);
                    } else {
                      // location.href = navigateTo;
                      // location.replace(document.referrer);
                      location.replace(process.env.VUE_APP_KRETA_TIMEOUT_REDIRECT_LANDING.concat(navigateTo));
                      this.disableAllCookieIfNeeded();
                      // this.$router.replace(navigateTo);
                    }
                  } else {
                    location.replace(process.env.VUE_APP_KRETA_TIMEOUT_REDIRECT_LANDING.concat(navigateTo));
                    // location.replace(document.referrer);
                    this.disableAllCookieIfNeeded();
                    // this.$router.replace(navigateTo);
                  }
                }
                resolve(payload);
                document.cookie =
                  'dkt-jwt-token=;expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; HostOnly; domain=.xeropan.com; path=/';
              } else {
                this.setMobileSupportModalStatus(false);
                document.cookie =
                  'dkt-jwt-token=;expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; HostOnly; domain=.xeropan.com; path=/';
                reject();
              }
            })
            .catch((error) => {
              this.setMobileSupportModalStatus(false);
              document.cookie =
                'dkt-jwt-token=;expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; HostOnly; domain=.xeropan.com; path=/';
              if (error.code === 618) {
                this.handleTeacherLogin();
              }
              reject();
            });
        } else {
          this.setMobileSupportModalStatus(false);
          reject();
        }
      });
    },
    handle2AFlowMethod() {
      Promise.all([
        this.getClassroomsByStudent(),
        this.getAssignmentByDktId(this.getDktOptions.dktAssignmentId),
      ])
        .then((resArray) => {
          const selectedAssignment = resArray[1];
          if (!selectedAssignment) {
            this.setDktOptions({
              dktAssignmentId: undefined,
              assignmentId: undefined,
              lessonId: undefined,
              classId: undefined,
            });
            location.href = '/';
            return;
          }
          this.setDktOptions({
            dktAssignmentId: undefined,
            assignmentId: selectedAssignment._id,
            classId: selectedAssignment.belongsToClass,
          });
          this.getLesson(this.getDktOptions.lessonId).then((lesson) => {
            if (lesson) {
              const additionalData = {
                assignmentId: selectedAssignment._id,
                classId: selectedAssignment.belongsToClass,
              };
              this.startLesson(lesson, additionalData);
            }
          });
        })
        .catch(() => {
          this.setDktOptions({
            dktAssignmentId: undefined,
            assignmentId: undefined,
            lessonId: undefined,
            classId: undefined,
          });
          location.href = '/';
        });
    },
    handleTeacherLogin() {
      let navigateTo;
      if (this.$route.name === 'DKTAuthenticationScreen') {
        navigateTo = '/';
      }

      this.$eventBus.showModal({
        bind: {
          is: GenericModal,
          hasExit: false,
          lock: true,
          titleText: this.$t('app.dkt.teacherLoginModal.title'),
          description: this.$t('app.dkt.teacherLoginModal.description'),
          okText: this.$t('app.dkt.teacherLoginModal.okButton'),
          okAction: () => {
            this.$eventBus.closeModal();
            if (navigateTo) {
              this.$router.replace(navigateTo);
            }
          },
        },
      });
    },
    showGeneralErrorModal(redirectTo = undefined) {
      this.$eventBus.showModal({
        bind: {
          is: GenericModal,
          lock: true,
          hasExit: false,
          titleText: this.$t('app.dkt.generalError.title'),
          description: this.$t('app.dkt.generalError.description'),
          okText: this.$t('app.dkt.generalError.okText'),
          okAction: () => {
            this.$eventBus.closeModal();
            this.navigateBackFromDKTSso(redirectTo);
          },
          escAction: () => {
            this.$eventBus.closeModal();
            location.replace('/');
          },
        },
      });
    },
    navigateBackFromDKTSso(redirectTo) {
      const isDktScreen = location.pathname.split('/')[1].includes('dkt');
      if (isDktScreen) {
        let url = redirectTo;
        if (!redirectTo || redirectTo.toString().includes('dkt')) {
          url = '/';
        }
        location.replace(url);
      }
    },
    transferAccounts(response) {
      this.$eventBus.showModal({
        bind: {
          is: KretaAccountTransferModal,
          newUser: response,
          lock: true,
          closeable: false,
        },
        on: {
          accountTransferredSuccessful: async () => {
            this.$eventBus.closeModal();
            await this.setDktUserFromResponse(response);
          },
        },
      });
    },
    async setDktUserFromResponse(
      response,
      accounTransfer = true,
      need2AFlowValidating = true,
    ) {
      return new Promise(async (resolve) => {
        const dktOptions = this.getDktOptions;
        if (accounTransfer) {
          await this.logoutUserMethod();
        }
        this.deleteAllCookies();
        window.localStorage.clear();
        // Explicit session timeout cookie
        cookieHandler.setCookie('session_timeout', false);
        this.setProfileUser(response);
        this.storeToken(response.token);
        if (response.deviceToken) {
          this.setApiToken(response.deviceToken);
        }
        await this.updateUserLanguage(dktOptions);
        if (dktOptions.needToSaveOptions) {
          this.setDktOptions(dktOptions);
        }
        resolve();
        if (
          need2AFlowValidating &&
          dktOptions &&
          dktOptions.lessonId &&
          dktOptions.dktAssignmentId
        ) {
          this.handle2AFlowMethod();
        } else if (accounTransfer) {
          if (response.registrationStatus === 'registered') {
            this.setDktOptions({
              fromDkt: false,
            });
            if (location.pathname.split('/')[1].includes('dkt')) {
              location.href = `${process.env.VUE_APP_KRETA_TIMEOUT_REDIRECT_LANDING}/`;
              this.disableAllCookieIfNeeded();
            } else {
              location.replace(`${process.env.VUE_APP_KRETA_TIMEOUT_REDIRECT_LANDING}${location.pathname}`);
              this.disableAllCookieIfNeeded();
            }
          } else {
            // location.href = '/onboarding';
            this.$router.replace(`${process.env.VUE_APP_KRETA_TIMEOUT_REDIRECT_LANDING}/onboarding`);
          }
        }
        if (dktOptions?.dktAssignmentId && dktOptions?.lessonId) {
          this.setDktOptions({
            needIntroScreen: true,
          });
        }
      });
    },
    startCheckinToKretaFlow() {
      if (!this.isKretaUser) {
        this.$eventBus.showModal({
          bind: {
            is: GenericModal,
            hasExit: false,
            titleText: this.$t('app.dkt.checkinToKretaFlow.modal.title'),
            description: this.$t(
              'app.dkt.checkinToKretaFlow.modal.description',
            ),
            okText: this.$t('app.dkt.checkinToKretaFlow.modal.okText'),
            cancelText: this.$t('app.dkt.checkinToKretaFlow.modal.cancelText'),
            lock: true,
            okAction: () => {
              this.dktRedirect();
              this.$eventBus.closeModal();
            },
            cancelAction: () => {
              this.$eventBus.closeModal();
            },
            escAction: () => {
              this.$eventBus.closeModal();
            },
          },
        });
      } else {
        this.dktRedirect();
      }
    },
  },
};
</script>
